import React from "react";
import { Box, Button, IconButton, Stack, Typography } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import SpecsModelInputField from "./components/editors/SpecsModelInputField";
import { useSpecsEditorContextValue } from "./useSpecsEditor";
import { useSpecsModel, useSpecsModelSelector } from "./SpecsModelContext";
import {
  getSelectedPositionForVersion,
  mutateSelectedPositionForVersion,
} from "./positionEditorModel";

export default function SpecsPositionTechpackSection() {
  const specsModel = useSpecsModel();
  const {
    editor: {
      identity: { productVersion },
    },
    selectedPositionId,
  } = useSpecsEditorContextValue();
  const conditionalPatterns = useSpecsModelSelector(
    (model) =>
      getSelectedPositionForVersion(
        model,
        productVersion,
        selectedPositionId
      )?.techpack?.conditionalPattern?.map((pattern: any) => ({
        ...pattern,
      })) ?? []
  );
  const selectedPositionPath = useSpecsModelSelector((model) => {
    const versionIndex = model.product.versions.findIndex(
      (version) => version.name === productVersion
    );
    const version = model.product.versions[versionIndex];

    if (!version) {
      return null;
    }

    const positionIndex = version.positions.findIndex(
      (position: any) => position?.id === selectedPositionId
    );

    if (positionIndex === -1) {
      return null;
    }

    return `window.specs.model.product.versions[${versionIndex}].positions[${positionIndex}]`;
  });

  const mutateSelectedPosition = React.useCallback(
    (mutator: (position: any) => void) => {
      mutateSelectedPositionForVersion(
        specsModel,
        productVersion,
        selectedPositionId,
        mutator
      );
    },
    [productVersion, selectedPositionId, specsModel]
  );

  return (
    <>
      <Typography variant="subtitle2">Techpack</Typography>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr 1fr", md: "repeat(4, 1fr)" },
          gap: 1.5,
        }}
      >
        <SpecsModelInputField
          label="Position"
          debugPath={
            selectedPositionPath
              ? `${selectedPositionPath}.techpack.position`
              : undefined
          }
          size="small"
          selector={(model) =>
            getSelectedPositionForVersion(
              model,
              productVersion,
              selectedPositionId
            )?.techpack?.position ?? ""
          }
          setValue={(_model, nextValue) =>
            mutateSelectedPosition((position) => {
              position.techpack = {
                ...(position.techpack ?? {}),
                position: nextValue,
              };
            })
          }
        />
        <SpecsModelInputField
          label="View"
          debugPath={
            selectedPositionPath
              ? `${selectedPositionPath}.techpack.view`
              : undefined
          }
          size="small"
          selector={(model) =>
            getSelectedPositionForVersion(
              model,
              productVersion,
              selectedPositionId
            )?.techpack?.view ?? ""
          }
          setValue={(_model, nextValue) =>
            mutateSelectedPosition((position) => {
              position.techpack = {
                ...(position.techpack ?? {}),
                view: nextValue,
              };
            })
          }
        />
        <SpecsModelInputField
          label="Description"
          debugPath={
            selectedPositionPath
              ? `${selectedPositionPath}.techpack.description`
              : undefined
          }
          size="small"
          selector={(model) =>
            getSelectedPositionForVersion(
              model,
              productVersion,
              selectedPositionId
            )?.techpack?.description ?? ""
          }
          setValue={(_model, nextValue) =>
            mutateSelectedPosition((position) => {
              position.techpack = {
                ...(position.techpack ?? {}),
                description: nextValue,
              };
            })
          }
        />
        <SpecsModelInputField
          label="Tag"
          debugPath={
            selectedPositionPath
              ? `${selectedPositionPath}.techpack.tag`
              : undefined
          }
          size="small"
          selector={(model) =>
            getSelectedPositionForVersion(
              model,
              productVersion,
              selectedPositionId
            )?.techpack?.tag ?? ""
          }
          setValue={(_model, nextValue) =>
            mutateSelectedPosition((position) => {
              position.techpack = {
                ...(position.techpack ?? {}),
                tag: nextValue,
              };
            })
          }
        />
      </Box>

      <Typography variant="subtitle2">Conditional Pattern</Typography>
      <Stack spacing={1.5}>
        {conditionalPatterns.map((pattern: any, index: number) => (
          <Box
            key={`pattern-${index}`}
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "1fr 1fr auto" },
              gap: 1.5,
              alignItems: "center",
            }}
          >
            <SpecsModelInputField
              label="When"
              debugPath={
                selectedPositionPath
                  ? `${selectedPositionPath}.techpack.conditionalPattern[${index}].when`
                  : undefined
              }
              size="small"
              selector={(model) =>
                getSelectedPositionForVersion(
                  model,
                  productVersion,
                  selectedPositionId
                )?.techpack?.conditionalPattern?.[index]?.when ?? ""
              }
              setValue={(_model, nextValue) =>
                mutateSelectedPosition((position) => {
                  const nextPatterns = [
                    ...((position.techpack?.conditionalPattern as any[]) ?? []),
                  ];
                  const currentPattern = nextPatterns[index] ?? {
                    when: "",
                    value: "",
                  };
                  nextPatterns[index] = {
                    ...currentPattern,
                    when: nextValue,
                  };
                  position.techpack = {
                    ...(position.techpack ?? {}),
                    conditionalPattern: nextPatterns,
                  };
                })
              }
            />
            <SpecsModelInputField
              label="Value"
              debugPath={
                selectedPositionPath
                  ? `${selectedPositionPath}.techpack.conditionalPattern[${index}].value`
                  : undefined
              }
              size="small"
              selector={(model) =>
                getSelectedPositionForVersion(
                  model,
                  productVersion,
                  selectedPositionId
                )?.techpack?.conditionalPattern?.[index]?.value ?? ""
              }
              setValue={(_model, nextValue) =>
                mutateSelectedPosition((position) => {
                  const nextPatterns = [
                    ...((position.techpack?.conditionalPattern as any[]) ?? []),
                  ];
                  const currentPattern = nextPatterns[index] ?? {
                    when: "",
                    value: "",
                  };
                  nextPatterns[index] = {
                    ...currentPattern,
                    value: nextValue,
                  };
                  position.techpack = {
                    ...(position.techpack ?? {}),
                    conditionalPattern: nextPatterns,
                  };
                })
              }
            />
            <IconButton
              onClick={() =>
                mutateSelectedPosition((position) => {
                  position.techpack = {
                    ...(position.techpack ?? {}),
                    conditionalPattern: conditionalPatterns.filter(
                      (_pattern: any, patternIndex: number) =>
                        patternIndex !== index
                    ),
                  };
                })
              }
            >
              <DeleteIcon />
            </IconButton>
          </Box>
        ))}
        <Button
          size="small"
          startIcon={<AddIcon />}
          onClick={() =>
            mutateSelectedPosition((position) => {
              position.techpack = {
                ...(position.techpack ?? {}),
                conditionalPattern: [
                  ...conditionalPatterns,
                  { when: "", value: "" },
                ],
              };
            })
          }
        >
          Add pattern
        </Button>
      </Stack>
    </>
  );
}
